import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useDeferredValue, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";

import {
  COMPROBANTE_CLASSES,
  IIBB_JURISDICTIONS,
  LINE_KIND_LABELS,
  LINE_KINDS,
  PURCHASE_KIND_LABELS,
  PURCHASE_KINDS,
  VAT_RATES,
  VOUCHER_TYPES,
  comprobanteCode,
  comprobanteOf,
  type LineKind,
  type PurchaseKind,
} from "@devengo/shared";

import { SearchSelect } from "../components/search-select";
import { Button, Card, ErrorBox, Field, Input, Notice, PageTitle, Select } from "../components/ui";
import { api } from "../lib/api";
import {
  centsDecimal,
  decimalCents,
  decimalToInput,
  formatCents,
  formatCuit,
  parseUserAmount,
  parseUserRate,
  voucherNumber,
} from "../lib/format";
import type { VoucherView } from "../lib/hooks";
import { parsePeriod, periodParam } from "../lib/session";
import {
  EMPTY_AMOUNTS,
  FIXED_RATES,
  fixedFromView,
  fixedLines,
  suggestedTax,
  type FixedAmounts,
} from "../lib/voucher-fixed";

interface LineForm {
  kind: LineKind;
  rate: string;
  net: string;
  tax: string;
  /** IIBB perceptions: Convenio Multilateral code as text. */
  jurisdiction: string;
}

interface VoucherForm {
  direction: "compra" | "venta";
  /** Detailed mode: the ARCA code. */
  typeCode: string;
  /** Fixed mode: comprobante class and letter, as the previous system asks for them. */
  classId: string;
  letter: string;
  issueDate: string;
  period: string;
  pointOfSale: string;
  number: string;
  docType: string;
  docNumber: string;
  name: string;
  currency: string;
  exchangeRate: string;
  cae: string;
  total: string;
  /** Empty: not stated (vouchers imported or loaded before plan 016 keep it so). */
  purchaseKind: PurchaseKind | "";
  lines: LineForm[];
  amounts: FixedAmounts;
}

export interface VoucherFormSearch {
  id?: string;
  dir?: "compra" | "venta";
  periodo?: string;
  /** Open in the line-by-line mode. */
  detalle?: boolean;
}

/**
 * Two ways to type a voucher (plan 016). "fijo" is the previous system's screen — one neto
 * gravado, the IVA of each rate, perceptions — and the default. "detalle" is line by line, for what
 * that screen cannot hold: rates 0 / 2,5 / 5, other taxes, foreign currency.
 */
type Mode = "fijo" | "detalle";

const emptyLine = (): LineForm => ({
  kind: "gravado",
  rate: "21",
  net: "",
  tax: "",
  jurisdiction: "902",
});

/** Purchases B, C and consumer tiques discriminate no IVA: the neto is no gravado. */
const NO_IVA_LETTERS = new Set(["B", "C", "-"]);

function defaults(search: VoucherFormSearch): VoucherForm {
  const { year, month } = parsePeriod(search.periodo);
  const period = periodParam(year, month);
  return {
    direction: search.dir ?? "compra",
    typeCode: "1",
    classId: "F",
    letter: "A",
    issueDate: `${period}-01`,
    period,
    pointOfSale: "",
    number: "",
    docType: "80",
    docNumber: "",
    name: "",
    currency: "ARS",
    exchangeRate: "1",
    cae: "",
    total: "",
    purchaseKind: "bienes",
    lines: [emptyLine()],
    amounts: { ...EMPTY_AMOUNTS },
  };
}

function fromView(v: VoucherView, period: string): VoucherForm {
  const comprobante = comprobanteOf(v.typeCode);
  return {
    direction: v.direction,
    typeCode: String(v.typeCode),
    classId: comprobante?.classId ?? "F",
    letter: comprobante?.letter ?? "A",
    issueDate: v.issueDate,
    period,
    pointOfSale: String(v.pointOfSale),
    number: String(v.number),
    docType: String(v.counterpart.docType),
    docNumber: v.counterpart.docNumber,
    name: v.counterpart.name,
    currency: v.currency,
    exchangeRate: v.exchangeRate.replace(".", ","),
    cae: v.cae ?? "",
    total: decimalToInput(v.total),
    purchaseKind: v.purchaseKind ?? "",
    lines: v.lines.map((l) => ({
      kind: l.kind as LineKind,
      rate: l.rate ?? "21",
      net: decimalToInput(l.net),
      tax: decimalToInput(l.tax),
      jurisdiction: l.jurisdiction === null ? "" : String(l.jurisdiction),
    })),
    amounts: fixedFromView(v) ?? { ...EMPTY_AMOUNTS },
  };
}

/** The detailed lines as the API takes them. */
function detailLines(form: VoucherForm, amount: (label: string, value: string) => string) {
  return form.lines.map((line, i) => ({
    kind: line.kind,
    ...(line.kind === "gravado" ? { rate: line.rate } : {}),
    net:
      line.kind === "gravado" || line.kind === "exento" || line.kind === "no_gravado"
        ? amount(`la línea ${i + 1}`, line.net)
        : "0",
    tax:
      line.kind === "exento" || line.kind === "no_gravado"
        ? "0"
        : amount(`la línea ${i + 1}`, line.tax),
    ...(line.kind === "percepcion_iibb" && line.jurisdiction
      ? { jurisdiction: Number(line.jurisdiction) }
      : {}),
  }));
}

function isNoIva(form: Pick<VoucherForm, "direction" | "letter">): boolean {
  return form.direction === "compra" && NO_IVA_LETTERS.has(form.letter);
}

/** Builds the API payload; returns the list of fields it could not read as amounts. */
function toPayload(form: VoucherForm, mode: Mode): { payload?: object; problems: string[] } {
  const problems: string[] = [];
  const amount = (label: string, value: string) => {
    const parsed = parseUserAmount(value);
    if (parsed === null)
      problems.push(`No se entiende el importe "${value}" en ${label}. Usá el formato 1.234,56.`);
    return parsed ?? "0";
  };
  // Ventas always go to the issue month (the API refuses anything else).
  const { year, month } = parsePeriod(
    form.direction === "venta" ? form.issueDate.slice(0, 7) : form.period,
  );
  let typeCode = Number(form.typeCode);
  let lines: object[];
  let total: string;
  if (mode === "fijo") {
    const code = comprobanteCode(form.classId, form.letter);
    if (code === undefined) problems.push("Ese comprobante no existe con esa letra.");
    typeCode = code ?? 0;
    const fixed = fixedLines(form.amounts, isNoIva(form));
    problems.push(...fixed.problems);
    lines = fixed.lines;
    total =
      form.total.trim() === "" ? centsDecimal(fixed.sumCents) : amount("el total", form.total);
  } else {
    lines = detailLines(form, amount);
    total = amount("el total", form.total);
  }
  const payload = {
    direction: form.direction,
    typeCode,
    issueDate: form.issueDate,
    period: { year, month },
    pointOfSale: Number(form.pointOfSale),
    number: Number(form.number),
    counterpart: { docType: Number(form.docType), docNumber: form.docNumber, name: form.name },
    currency: mode === "fijo" ? "ARS" : form.currency,
    exchangeRate:
      mode === "fijo" || form.currency === "ARS"
        ? "1"
        : (parseUserRate(form.exchangeRate) ??
          (problems.push(
            `No se entiende el tipo de cambio "${form.exchangeRate}". Usá el formato 1.234,50.`,
          ),
          "0")),
    total,
    ...(form.cae ? { cae: form.cae } : {}),
    ...(form.direction === "compra" && form.purchaseKind
      ? { purchaseKind: form.purchaseKind }
      : {}),
    lines,
  };
  return problems.length ? { problems } : { payload, problems };
}

const DOC_LABEL: Record<number, string> = { 80: "CUIT", 86: "CUIL", 96: "DNI" };

/** One row of the fixed screen: label, input, and the key hint the previous system shows. */
function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    // A label, so the text names the row's first field (and a click on it focuses that field).
    <label className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[11rem_minmax(0,18rem)_1fr] sm:gap-3">
      <span className="text-sm font-medium text-brand-text">{label}</span>
      <div>{children}</div>
      <div className="text-xs text-brand-muted">{hint}</div>
    </label>
  );
}

export function VoucherFormPage({
  companyId,
  search,
}: {
  companyId: string;
  search: VoucherFormSearch;
}) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const editing = Boolean(search.id);
  const existing = useQuery({
    queryKey: ["voucher", search.id],
    queryFn: () => api.get<VoucherView>(`/api/vouchers/${search.id}`),
    enabled: editing,
  });
  const form = useForm<VoucherForm>({ defaultValues: defaults(search) });
  const lines = useFieldArray({ control: form.control, name: "lines" });
  const watched = useWatch({ control: form.control });
  const [mode, setMode] = useState<Mode>(search.detalle ? "detalle" : "fijo");
  const [modeNote, setModeNote] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ voucher: VoucherView; warnings: string[] } | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  // The counterpart chosen from the list — or the one an edited voucher already names: typing
  // another name afterwards means another counterpart, whose document must be entered again.
  const [picked, setPicked] = useState<{ name: string; docNumber: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  // The period the user works in (from the list, or typed): a purchase dated earlier stays in it,
  // as in the previous system; a later date moves the voucher forward, and correcting the date
  // back returns it here (Codex 016 final round).
  const workingPeriod = useRef(defaults(search).period);
  const followDate = (date: string) => {
    const month = date.slice(0, 7);
    const venta = form.getValues("direction") === "venta";
    form.setValue("period", venta || month > workingPeriod.current ? month : workingPeriod.current);
  };

  useEffect(() => {
    if (!existing.data) return;
    form.reset(fromView(existing.data, search.periodo ?? existing.data.issueDate.slice(0, 7)));
    setPicked({
      name: existing.data.counterpart.name,
      docNumber: existing.data.counterpart.docNumber,
    });
    if (!search.detalle && fixedFromView(existing.data) === null) {
      setMode("detalle");
      setModeNote(
        "Este comprobante tiene importes que la carga simple no muestra tal cual (otra alícuota, otros tributos, otra moneda o netos cargados línea por línea): se edita con el detalle por línea.",
      );
    }
  }, [existing.data, form, search.periodo, search.detalle]);

  const save = useMutation({
    mutationFn: (payload: object) =>
      editing
        ? api.put<{ voucher: VoucherView; warnings: string[] }>(
            `/api/vouchers/${search.id}`,
            payload,
          )
        : api.post<{ voucher: VoucherView; warnings: string[] }>(
            `/api/companies/${companyId}/vouchers`,
            payload,
          ),
    onSuccess: async (result) => {
      await client.invalidateQueries({ queryKey: ["vouchers", companyId] });
      await client.invalidateQueries({ queryKey: ["company-counterparts", companyId] });
      await client.invalidateQueries({ queryKey: ["periods", companyId] });
      await client.invalidateQueries({ queryKey: ["caja", companyId] });
      if (editing) {
        void navigate({
          to: "/empresas/$companyId/comprobantes",
          params: { companyId },
          search: { dir: result.voucher.direction, periodo: form.getValues("period") },
        });
        return;
      }
      setSaved(result);
      setPicked(null);
      const keep = form.getValues();
      form.reset({
        ...defaults(search),
        direction: keep.direction,
        typeCode: keep.typeCode,
        classId: keep.classId,
        letter: keep.letter,
        issueDate: keep.issueDate,
        period: keep.period,
        pointOfSale: keep.pointOfSale,
        purchaseKind: keep.purchaseKind,
        amounts: { ...EMPTY_AMOUNTS, jurisdiction: keep.amounts.jurisdiction },
      });
      // Next voucher: straight back to the counterpart, as in the previous system.
      formRef.current?.querySelector<HTMLInputElement>("[data-first-wrapper] input")?.focus();
    },
  });

  const detailSumCents = (watched.lines ?? []).reduce((acc, line) => {
    const net =
      line?.kind === "gravado" || line?.kind === "exento" || line?.kind === "no_gravado"
        ? parseUserAmount(line?.net ?? "")
        : "0";
    const tax =
      line?.kind === "exento" || line?.kind === "no_gravado"
        ? "0"
        : parseUserAmount(line?.tax ?? "");
    return acc + decimalCents(net ?? "0") + decimalCents(tax ?? "0");
  }, 0);
  const amounts = { ...EMPTY_AMOUNTS, ...watched.amounts } as FixedAmounts;
  const noIva = isNoIva({
    direction: watched.direction ?? "compra",
    letter: watched.letter ?? "A",
  });
  const fixed = fixedLines(amounts, noIva);

  // The company's own clients (sales) or suppliers (purchases), for the search box.
  const role = watched.direction === "venta" ? "cliente" : "proveedor";
  // Searched on the server, so a company with hundreds of suppliers still finds every one.
  const searchText = useDeferredValue((watched.name ?? "").trim());
  const companyCounterparts = useQuery({
    queryKey: ["company-counterparts", companyId, role, searchText],
    queryFn: () =>
      api.get<{ id: string; docType: number; docNumber: string; name: string }[]>(
        `/api/companies/${companyId}/counterparts?tipo=${role}${searchText ? `&q=${encodeURIComponent(searchText)}` : ""}`,
      ),
    placeholderData: (previous) => previous,
  });

  const lookupCounterpart = async () => {
    const doc = form.getValues("docNumber").replace(/\D/g, "");
    if (!doc || form.getValues("name")) return;
    const found = await api.get<{ docNumber: string; name: string }[]>(
      `/api/counterparts?q=${doc}`,
    );
    const match = found.find((c) => c.docNumber === doc);
    if (match) form.setValue("name", match.name);
  };

  const fillTax = (index: number) => {
    const line = form.getValues(`lines.${index}`);
    if (line.kind !== "gravado" || line.tax) return;
    const net = parseUserAmount(line.net);
    if (net === null) return;
    const bp = Math.round(Number(line.rate) * 100);
    const tax = Math.round((decimalCents(net) * bp) / 10_000);
    form.setValue(`lines.${index}.tax`, decimalToInput(centsDecimal(tax)));
  };

  const fillFixedTax = (key: (typeof FIXED_RATES)[number]["key"]) => {
    const rate = FIXED_RATES.find((r) => r.key === key)?.rate;
    if (!rate) return;
    const tax = suggestedTax(form.getValues("amounts"), rate);
    if (tax !== null) form.setValue(`amounts.${key}`, decimalToInput(tax));
  };

  /**
   * The IVA rows' arrows, as Maxi asked (2026-10-21): right computes the IVA, left takes it back
   * to 0, up and down go to the previous and next field.
   */
  const onFixedTaxKey = (
    e: KeyboardEvent<HTMLInputElement>,
    key: (typeof FIXED_RATES)[number]["key"],
  ) => {
    // Shift+arrows still select text (Codex 022).
    if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.key === "ArrowRight") fillFixedTax(key);
    else if (e.key === "ArrowLeft") form.setValue(`amounts.${key}`, "");
    else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const fields = [
        ...(e.currentTarget.form?.querySelectorAll<HTMLElement>(
          "[data-nav], [data-first-wrapper] input",
        ) ?? []),
      ].filter((el) => !(el as HTMLInputElement).disabled);
      const at = fields.indexOf(e.currentTarget);
      fields[e.key === "ArrowUp" ? at - 1 : at + 1]?.focus();
    } else return;
    e.preventDefault();
  };

  /** Switching modes carries the amounts over; refused when they do not fit the other mode. */
  const switchMode = (to: Mode) => {
    const values = form.getValues();
    if (to === "detalle") {
      const result = fixedLines(values.amounts, isNoIva(values));
      if (result.problems.length) {
        setProblems(result.problems);
        return;
      }
      const code = comprobanteCode(values.classId, values.letter);
      if (code !== undefined) form.setValue("typeCode", String(code));
      // The fixed screen takes an empty total as the sum; the detailed one needs it written.
      if (values.total.trim() === "") {
        form.setValue("total", decimalToInput(centsDecimal(result.sumCents)));
      }
      form.setValue(
        "lines",
        result.lines.length
          ? result.lines.map((l) => ({
              kind: l.kind,
              rate: l.rate ?? "21",
              net: decimalToInput(l.net),
              tax: decimalToInput(l.tax),
              jurisdiction:
                l.kind === "percepcion_iibb" ? (values.amounts.jurisdiction ?? "") : "902",
            }))
          : [emptyLine()],
      );
    } else {
      const comprobante = comprobanteOf(Number(values.typeCode));
      const problems: string[] = [];
      const asView = {
        currency: values.currency,
        lines: values.lines.map((l, i) => {
          const net = parseUserAmount(l.net);
          const tax = parseUserAmount(l.tax);
          if (net === null || tax === null)
            problems.push(`No se entiende un importe de la línea ${i + 1}.`);
          return {
            lineNo: i + 1,
            kind: l.kind,
            rate: l.kind === "gravado" ? l.rate : null,
            net: net ?? "0",
            tax: tax ?? "0",
            jurisdiction:
              l.kind === "percepcion_iibb" && l.jurisdiction ? Number(l.jurisdiction) : null,
          };
        }),
      } as unknown as VoucherView;
      const fixedAmounts = problems.length ? null : fixedFromView(asView);
      if (!comprobante || !fixedAmounts) {
        setProblems([
          ...problems,
          "Estas líneas no entran en la carga simple (otra alícuota, otros tributos, otra moneda o netos que no salen del IVA): seguí con el detalle por línea.",
        ]);
        return;
      }
      form.setValue("classId", comprobante.classId);
      form.setValue("letter", comprobante.letter);
      form.setValue("amounts", fixedAmounts);
    }
    setProblems([]);
    setModeNote(null);
    setMode(to);
  };

  const submit = form.handleSubmit((values) => {
    setSaved(null);
    const { payload, problems: found } = toPayload(values, mode);
    setProblems(found);
    if (payload) save.mutate(payload);
  });

  /**
   * The previous system's keyboard: Enter goes to the next field and saves on the last one. A
   * search box that used Enter to pick an option has already handled it (defaultPrevented).
   */
  const onKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (mode !== "fijo" || e.key !== "Enter" || e.defaultPrevented) return;
    const target = e.target as HTMLElement;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    e.preventDefault();
    const fields = [
      ...e.currentTarget.querySelectorAll<HTMLElement>("[data-nav], [data-first-wrapper] input"),
    ].filter((el) => !(el as HTMLInputElement).disabled);
    const next = fields.find(
      (el) => target.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING,
    );
    if (next) next.focus();
    else void submit();
  };

  const letters = Object.keys(
    COMPROBANTE_CLASSES.find((c) => c.id === watched.classId)?.codes ?? {},
  );
  const isVenta = watched.direction === "venta";

  const counterpartPicker = (
    <Controller
      control={form.control}
      name="name"
      render={({ field }) => (
        <SearchSelect
          ariaLabel={isVenta ? "Cliente" : "Proveedor"}
          placeholder={
            isVenta ? "Buscar cliente por nombre o CUIT" : "Buscar proveedor por nombre o CUIT"
          }
          value={field.value ?? ""}
          onChange={(text) => {
            if (picked && text !== picked.name) {
              // A new name with the chosen one's document would save the wrong
              // counterpart (Codex review): the document has to be entered again.
              if (form.getValues("docNumber") === picked.docNumber) {
                form.setValue("docNumber", "");
              }
              setPicked(null);
            }
            field.onChange(text);
          }}
          options={(companyCounterparts.data ?? []).map((c) => ({
            id: c.id,
            label: c.name,
            detail:
              c.docType === 99
                ? "Consumidor final"
                : `${DOC_LABEL[c.docType] ?? "Doc."} ${formatCuit(c.docNumber)}`,
          }))}
          onPick={(option) => {
            const found = companyCounterparts.data?.find((c) => c.id === option.id);
            if (!found) return;
            form.setValue("docType", String(found.docType));
            form.setValue("docNumber", found.docNumber);
            field.onChange(found.name);
            setPicked({ name: found.name, docNumber: found.docNumber });
          }}
          emptyText={
            isVenta
              ? "No es cliente de esta empresa todavía: se agrega al guardar."
              : "No es proveedor de esta empresa todavía: se agrega al guardar."
          }
        />
      )}
    />
  );

  const docFields = (
    <div className="flex gap-2">
      <Select
        data-nav
        className="w-28"
        aria-label="Tipo de documento"
        {...form.register("docType")}
      >
        <option value="80">CUIT</option>
        <option value="86">CUIL</option>
        <option value="96">DNI</option>
        <option value="99">Consumidor final</option>
      </Select>
      <Input
        data-nav
        aria-label="Número de documento"
        placeholder="Número"
        {...form.register("docNumber", { onBlur: () => void lookupCounterpart() })}
      />
    </div>
  );

  const purchaseKindSelect = (
    <Select data-nav aria-label="Tipo de compra" {...form.register("purchaseKind")}>
      <option value="">Sin indicar</option>
      {PURCHASE_KINDS.map((k) => (
        <option key={k} value={k}>
          {PURCHASE_KIND_LABELS[k]}
        </option>
      ))}
    </Select>
  );

  const amountInput = (key: keyof Omit<FixedAmounts, "jurisdiction">, label: string) => (
    <Input
      data-nav
      aria-label={label}
      className="text-right tabular"
      placeholder="0,00"
      {...form.register(`amounts.${key}`)}
    />
  );

  return (
    <>
      <PageTitle
        subtitle={
          mode === "fijo"
            ? "Como en el sistema anterior: Enter pasa al campo siguiente. En un IVA, → lo calcula sobre el neto, ← lo vuelve a 0 y ↑ ↓ cambian de campo."
            : "Cada alícuota va en su propia línea: una factura con 21% y 10,5% se carga como un solo comprobante."
        }
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => switchMode(mode === "fijo" ? "detalle" : "fijo")}
            >
              {mode === "fijo" ? "Cargar con detalle por línea" : "Volver a la carga simple"}
            </Button>
            <Link
              to="/empresas/$companyId/comprobantes"
              params={{ companyId }}
              search={{
                dir: watched.direction ?? "compra",
                periodo: watched.period ?? search.periodo ?? "",
              }}
              className="text-sm text-brand-primary hover:underline"
            >
              Volver a la lista
            </Link>
          </div>
        }
      >
        {editing ? "Editar comprobante" : isVenta ? "Cargar venta" : "Cargar compra"}
      </PageTitle>

      {saved ? (
        <div className="mb-3 flex flex-col gap-2">
          <Notice tone="success">
            Guardado: {saved.voucher.typeName}{" "}
            {voucherNumber(saved.voucher.pointOfSale, saved.voucher.number)} de{" "}
            {saved.voucher.counterpart.name}. Podés cargar el siguiente.
          </Notice>
          {saved.warnings.map((w) => (
            <Notice key={w} tone="warning">
              Revisá: {w}
            </Notice>
          ))}
        </div>
      ) : null}
      {modeNote ? (
        <div className="mb-3">
          <Notice tone="warning">{modeNote}</Notice>
        </div>
      ) : null}

      <form ref={formRef} onSubmit={submit} onKeyDown={onKeyDown} className="flex flex-col gap-4">
        {mode === "fijo" ? (
          <Card title={isVenta ? "Ventas" : "Compras / Servicios"}>
            <div className="flex flex-col gap-2">
              <Row label="Operación">
                <Select data-nav {...form.register("direction")}>
                  <option value="compra">Compra</option>
                  <option value="venta">Venta</option>
                </Select>
              </Row>
              <Row label="Fecha">
                <Input
                  data-nav
                  type="date"
                  {...form.register("issueDate", {
                    onChange: (e: { target: { value: string } }) => {
                      if (!editing && e.target.value) followDate(e.target.value);
                    },
                  })}
                />
              </Row>
              <Row
                label="Período"
                hint={
                  editing
                    ? "Para cambiarlo usá «Mover» en la lista"
                    : isVenta
                      ? "Las ventas van en el mes de emisión"
                      : "Una compra se puede diferir a un mes posterior"
                }
              >
                <Input
                  data-nav
                  type="month"
                  disabled={editing || isVenta}
                  {...form.register("period", {
                    onChange: (e: { target: { value: string } }) => {
                      workingPeriod.current = e.target.value;
                    },
                  })}
                />
              </Row>
              <Row
                label={isVenta ? "Cliente" : "Proveedor"}
                hint="Buscá por nombre o CUIT; si es nuevo, escribí el nombre y el documento."
              >
                <div data-first-wrapper>{counterpartPicker}</div>
              </Row>
              <Row label="Documento">{docFields}</Row>
              <Row label="Comprobante">
                <Select
                  data-nav
                  aria-label="Comprobante"
                  {...form.register("classId", {
                    // A letter the new class does not have would stay hidden in the form and
                    // fail on save (Codex 016 M2): take the class's first letter instead.
                    onChange: (e: { target: { value: string } }) => {
                      const codes = COMPROBANTE_CLASSES.find((c) => c.id === e.target.value)?.codes;
                      const available = Object.keys(codes ?? {});
                      if (!available.includes(form.getValues("letter"))) {
                        form.setValue("letter", available[0] ?? "A");
                      }
                    },
                  })}
                >
                  {COMPROBANTE_CLASSES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.id} · {c.label}
                    </option>
                  ))}
                </Select>
              </Row>
              <Row label="Tipo">
                <Select data-nav aria-label="Letra" {...form.register("letter")}>
                  {letters.map((l) => (
                    <option key={l} value={l}>
                      {l === "-" ? "Sin letra" : l}
                    </option>
                  ))}
                </Select>
              </Row>
              <Row label="Punto de venta">
                <Input data-nav inputMode="numeric" {...form.register("pointOfSale")} />
              </Row>
              <Row label="Número">
                <Input data-nav inputMode="numeric" {...form.register("number")} />
              </Row>
              <Row
                label="Neto gravado"
                hint={noIva ? "Comprobante sin IVA discriminado: va como no gravado." : undefined}
              >
                {amountInput("neto", "Neto gravado")}
              </Row>
              <Row label="No gravado">{amountInput("noGravado", "No gravado")}</Row>
              <Row label="Exento">{amountInput("exento", "Exento")}</Row>
              {FIXED_RATES.map(({ key, label, rate }) => (
                <Row
                  key={key}
                  label={label}
                  hint={
                    noIva ? undefined : (
                      <button
                        type="button"
                        className="text-brand-primary hover:underline"
                        onClick={() => fillFixedTax(key)}
                      >
                        → calcula IVA {rate.replace(".", ",")}%
                      </button>
                    )
                  }
                >
                  <Input
                    data-nav
                    aria-label={label}
                    className="text-right tabular"
                    placeholder="0,00"
                    disabled={noIva}
                    {...form.register(`amounts.${key}`)}
                    onKeyDown={(e) => onFixedTaxKey(e, key)}
                  />
                </Row>
              ))}
              <Row label="Percepción IVA">{amountInput("percIva", "Percepción IVA")}</Row>
              <Row
                label="Percepción IB"
                hint={
                  <Select
                    data-nav
                    aria-label="Jurisdicción"
                    className="max-w-56"
                    {...form.register("amounts.jurisdiction")}
                  >
                    <option value="">Sin indicar</option>
                    {IIBB_JURISDICTIONS.map((j) => (
                      <option key={j.code} value={j.code}>
                        {j.code} - {j.name}
                      </option>
                    ))}
                  </Select>
                }
              >
                {amountInput("percIb", "Percepción IB")}
              </Row>
              <Row
                label="Total"
                hint={
                  watched.total?.trim()
                    ? `Suma de los importes: ${formatCents(fixed.sumCents)}`
                    : "Vacío: se toma la suma de los importes."
                }
              >
                <Input
                  data-nav
                  aria-label="Total"
                  className="text-right tabular font-semibold"
                  placeholder={formatCents(fixed.sumCents)}
                  {...form.register("total")}
                />
              </Row>
              {isVenta ? null : <Row label="Tipo de compra">{purchaseKindSelect}</Row>}
            </div>
            {fixed.split ? (
              <p className="mt-3 text-sm text-brand-muted">
                El neto se guarda repartido por alícuota según el IVA de cada una:{" "}
                {fixed.split
                  .map((l) => `$ ${formatCents(l.netCents)} al ${l.rate.replace(".", ",")}%`)
                  .join(" y ")}
                .
              </p>
            ) : null}
          </Card>
        ) : (
          <>
            <Card title="Datos del comprobante">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                <Field label="Operación">
                  <Select {...form.register("direction")}>
                    <option value="compra">Compra</option>
                    <option value="venta">Venta</option>
                  </Select>
                </Field>
                <div className="col-span-2">
                  <Field label="Tipo">
                    <Select {...form.register("typeCode")}>
                      {VOUCHER_TYPES.map((t) => (
                        <option key={t.code} value={t.code}>
                          {String(t.code).padStart(3, "0")} · {t.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>
                <Field label="Fecha">
                  <Input
                    type="date"
                    {...form.register("issueDate", {
                      onChange: (e: { target: { value: string } }) => {
                        if (!editing && e.target.value) followDate(e.target.value);
                      },
                    })}
                  />
                </Field>
                <Field
                  label="Período"
                  hint={
                    editing
                      ? "Para cambiarlo usá «Mover» en la lista"
                      : isVenta
                        ? "Las ventas van en el mes de emisión"
                        : "Una compra se puede diferir a un mes posterior"
                  }
                >
                  <Input
                    type="month"
                    disabled={editing || isVenta}
                    {...form.register("period", {
                      onChange: (e: { target: { value: string } }) => {
                        workingPeriod.current = e.target.value;
                      },
                    })}
                  />
                </Field>
                <Field label="CAE / CAI">
                  <Input {...form.register("cae")} />
                </Field>
                <Field label="Punto de venta">
                  <Input inputMode="numeric" {...form.register("pointOfSale")} />
                </Field>
                <Field label="Número">
                  <Input inputMode="numeric" {...form.register("number")} />
                </Field>
                <div className="col-span-2">
                  <Field label="Documento">{docFields}</Field>
                </div>
                <div className="col-span-2">
                  <Field
                    label={isVenta ? "Cliente" : "Proveedor"}
                    hint="Buscá entre los de esta empresa por nombre o CUIT; si es nuevo, completá documento y nombre."
                  >
                    {counterpartPicker}
                  </Field>
                </div>
                <Field label="Moneda">
                  <Select {...form.register("currency")}>
                    <option value="ARS">Pesos (ARS)</option>
                    <option value="USD">Dólares (USD)</option>
                    <option value="EUR">Euros (EUR)</option>
                  </Select>
                </Field>
                <Field
                  label="Tipo de cambio"
                  hint={
                    watched.currency === "ARS"
                      ? undefined
                      : `Se toma como $ ${parseUserRate(watched.exchangeRate ?? "") ?? "?"} por unidad`
                  }
                >
                  <Input {...form.register("exchangeRate")} disabled={watched.currency === "ARS"} />
                </Field>
                {isVenta ? null : <Field label="Tipo de compra">{purchaseKindSelect}</Field>}
              </div>
            </Card>

            <Card
              title="Importes"
              actions={
                <Button variant="secondary" onClick={() => lines.append(emptyLine())}>
                  Agregar línea
                </Button>
              }
            >
              <div className="flex flex-col gap-2">
                {lines.fields.map((field, index) => {
                  const kind = watched.lines?.[index]?.kind ?? "gravado";
                  const hasNet = kind === "gravado" || kind === "exento" || kind === "no_gravado";
                  const hasTax = !(kind === "exento" || kind === "no_gravado");
                  return (
                    <div key={field.id} className="grid grid-cols-12 items-end gap-2">
                      <div className="col-span-3">
                        <Field label={index === 0 ? "Concepto" : ""}>
                          <Select {...form.register(`lines.${index}.kind`)}>
                            {LINE_KINDS.map((k) => (
                              <option key={k} value={k}>
                                {LINE_KIND_LABELS[k]}
                              </option>
                            ))}
                          </Select>
                        </Field>
                      </div>
                      <div className="col-span-2">
                        <Field label={index === 0 ? "Alícuota / jurisdicción" : ""}>
                          {kind === "percepcion_iibb" ? (
                            <Select
                              aria-label={`Jurisdicción de la línea ${index + 1}`}
                              {...form.register(`lines.${index}.jurisdiction`)}
                            >
                              <option value="">Sin indicar</option>
                              {IIBB_JURISDICTIONS.map((j) => (
                                <option key={j.code} value={j.code}>
                                  {j.code} - {j.name}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <Select
                              {...form.register(`lines.${index}.rate`)}
                              disabled={kind !== "gravado"}
                            >
                              {VAT_RATES.map((r) => (
                                <option key={r} value={r}>
                                  {r.replace(".", ",")}%
                                </option>
                              ))}
                            </Select>
                          )}
                        </Field>
                      </div>
                      <div className="col-span-3">
                        <Field label={index === 0 ? "Neto" : ""}>
                          <Input
                            className="text-right tabular"
                            placeholder={hasNet ? "0,00" : "—"}
                            disabled={!hasNet}
                            {...form.register(`lines.${index}.net`, {
                              onBlur: () => fillTax(index),
                            })}
                          />
                        </Field>
                      </div>
                      <div className="col-span-3">
                        <Field label={index === 0 ? "IVA / importe del tributo" : ""}>
                          <Input
                            className="text-right tabular"
                            placeholder={hasTax ? "0,00" : "—"}
                            disabled={!hasTax}
                            {...form.register(`lines.${index}.tax`)}
                          />
                        </Field>
                      </div>
                      <div className="col-span-1 pb-1">
                        {lines.fields.length > 1 ? (
                          <Button
                            variant="ghost"
                            onClick={() => lines.remove(index)}
                            aria-label={`Quitar línea ${index + 1}`}
                          >
                            Quitar
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap items-end justify-end gap-3 border-t border-brand-line pt-3">
                <span className="pb-2 text-sm text-brand-muted">
                  Suma de las líneas:{" "}
                  <strong className="tabular text-brand-text">{formatCents(detailSumCents)}</strong>
                </span>
                <Button
                  variant="secondary"
                  onClick={() =>
                    form.setValue("total", decimalToInput(centsDecimal(detailSumCents)))
                  }
                >
                  Usar la suma
                </Button>
                <div className="w-48">
                  <Field label="Total del comprobante">
                    <Input
                      className="text-right tabular"
                      placeholder="0,00"
                      {...form.register("total")}
                    />
                  </Field>
                </div>
              </div>
            </Card>
          </>
        )}

        {problems.length ? (
          <div className="flex flex-col gap-1">
            {problems.map((p) => (
              <Notice key={p} tone="warning">
                {p}
              </Notice>
            ))}
          </div>
        ) : null}
        <ErrorBox error={save.error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={save.isPending}>
            {editing ? "Guardar cambios" : "Guardar comprobante"}
          </Button>
        </div>
      </form>
    </>
  );
}
